import React from 'react'
import Link from 'next/link'
import { FaTruck, FaTag } from 'react-icons/fa'
import { buildAirconPriceTables } from '../../../../js/aircon-cost'

// Effective per-unit rate once the transport fee that most Singapore
// providers charge separately is excluded from our all-in price.
// Exported so the price page derives its "from $X/unit" claim from the same figure.
export const TRANSPORT = 20

// Singapore general servicing totals for 1-6 wall units, from the pricing
// profile (data/aircon-pricing-profiles.js) so they follow a price change.
export const airconPriceTiers = buildAirconPriceTables('sg', 6).general
    .map(({ units, wall }) => ({ units, total: wall }))

const perUnit = (total, units) => (total - TRANSPORT) / units

// UAE / Malaysia: pass `copy` (title, subtitle, paragraphs, caption,
// footnote, priceLinkLabel), `tiers` [{ units, total }] from the market's
// price tables, `money` (formats a price) and `priceHref`. Their prices
// include travel, so there is no transport column to subtract.
const MarketPriceComparison = ({ copy, tiers, money, priceHref }) => {
    const rows = tiers.map((tier) => ({ ...tier, rate: tier.total / tier.units }))
    const best = rows.reduce((a, b) => (b.rate < a.rate ? b : a))
    return (
        <section className="py-8 md:py-14 bg-gray-50" aria-labelledby="aircon-price-proof">
            <div className="max-w-4xl mx-auto px-4">
                <div className="text-center mb-8">
                    <h2 id="aircon-price-proof" className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
                        {copy.title}
                    </h2>
                    <h3 className="text-base md:text-lg font-semibold text-orange-600 mt-3">
                        {copy.subtitle}
                    </h3>
                    <div className="text-gray-600 leading-relaxed mt-4 max-w-2xl mx-auto space-y-3 text-sm md:text-base">
                        {copy.paragraphs.map((text) => <p key={text}>{text}</p>)}
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <table className="w-full text-sm text-left">
                        <caption className="sr-only">{copy.caption}</caption>
                        <thead className="bg-gray-50 text-gray-700">
                            <tr>
                                <th scope="col" className="px-3 md:px-4 py-3 font-semibold">Units</th>
                                <th scope="col" className="px-3 md:px-4 py-3 font-semibold">You pay</th>
                                <th scope="col" className="hidden md:table-cell px-4 py-3 font-semibold">Travel</th>
                                <th scope="col" className="px-3 md:px-4 py-3 font-semibold">Per unit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((row) => {
                                const isBest = row.units === best.units
                                return (
                                    <tr key={row.units} className={isBest ? 'bg-orange-50/60' : ''}>
                                        <th scope="row" className="px-3 md:px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                            {row.units} {row.units === 1 ? 'unit' : 'units'}
                                        </th>
                                        <td className="px-3 md:px-4 py-3 text-gray-700 whitespace-nowrap">{money(row.total)}</td>
                                        <td className="hidden md:table-cell px-4 py-3 text-green-600">Included</td>
                                        <td className={`px-3 md:px-4 py-3 font-bold whitespace-nowrap ${isBest ? 'text-orange-600' : 'text-gray-900'}`}>
                                            {money(row.rate)}
                                            {isBest && <span className="block md:inline md:ml-2 text-2xs md:text-xs font-semibold uppercase">Best rate</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>

                    <div className="border-t border-gray-100 p-5 md:p-6">
                        <div className="flex items-start gap-3">
                            <FaTag className="text-orange-500 mt-1 shrink-0" />
                            <p className="text-sm text-gray-600">
                                {copy.footnote}{' '}
                                {priceHref && (
                                    <>
                                        <Link href={priceHref} prefetch={false} className="text-orange-600 font-semibold hover:underline">
                                            {copy.priceLinkLabel}
                                        </Link>.
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export const AirconPriceComparison = (props) => {
    if (props.copy) return <MarketPriceComparison {...props} />
    const best = airconPriceTiers.reduce((a, b) =>
        perUnit(b.total, b.units) < perUnit(a.total, a.units) ? b : a
    )

    return (
        <section className="py-8 md:py-14 bg-gray-50" aria-labelledby="aircon-price-proof">
            <div className="max-w-4xl mx-auto px-4">
                <div className="text-center mb-8">
                    {/* <div className="inline-flex items-center gap-2 bg-white border border-orange-200/60 px-4 py-1.5 rounded-full mb-5 shadow-sm">
                        <FaTruck className="text-orange-500 text-xs" />
                        <span className="text-xs font-semibold text-orange-600 uppercase tracking-wider">
                            Transport Included
                        </span>
                    </div> */}
                    <h2 id="aircon-price-proof" className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
                        Affordable Aircon Servicing Prices in Singapore
                    </h2>
                    <h3 className="text-base md:text-lg font-semibold text-orange-600 mt-3">
                        Transport included in every price
                    </h3>
                    {/* Lead-in prose merged from the former standalone intro section -
                        both covered price and affordability but were separated by the
                        deals block, splitting the topic across two weaker sections. */}
                    <div className="text-gray-600 leading-relaxed mt-4 max-w-2xl mx-auto space-y-3 text-sm md:text-base">
                        <p>
                            StringsSG offers affordable, certified aircon servicing and repair without compromising
                            on service quality. Services include aircon cleaning and maintenance, chemical wash and
                            chemical overhaul, aircon repair, compressor cleaning and refrigerant gas top up for
                            HDB flats, BTO and resale units, condominiums and commercial premises.
                        </p>
                        <p>
                            Aircon servicing in Singapore is usually quoted per unit, with a separate transport or
                            callout fee added on top. Our prices already include transport, so the figure you see is
                            the figure you pay. The last column shows what each unit works out to once transport
                            is set aside.
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <div>
                        <table className="w-full text-sm text-left">
                            <caption className="sr-only">
                                Aircon general servicing prices in Singapore by number of units, including transport
                            </caption>
                            <thead className="bg-gray-50 text-gray-700">
                                <tr>
                                    <th scope="col" className="px-3 md:px-4 py-3 font-semibold">Units</th>
                                    <th scope="col" className="px-3 md:px-4 py-3 font-semibold">You pay</th>
                                    <th scope="col" className="hidden md:table-cell px-4 py-3 font-semibold">Transport</th>
                                    <th scope="col" className="px-3 md:px-4 py-3 font-semibold">Per unit</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {airconPriceTiers.map((tier) => {
                                    const rate = perUnit(tier.total, tier.units)
                                    const isBest = tier.units === best.units
                                    return (
                                        <tr key={tier.units} className={isBest ? 'bg-orange-50/60' : ''}>
                                            <th scope="row" className="px-3 md:px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                {tier.units} {tier.units === 1 ? 'unit' : 'units'}
                                            </th>
                                            <td className="px-3 md:px-4 py-3 text-gray-700 whitespace-nowrap">${tier.total.toFixed(2)}</td>
                                            <td className="hidden md:table-cell px-4 py-3 text-green-600">Included</td>
                                            <td className={`px-3 md:px-4 py-3 font-bold whitespace-nowrap ${isBest ? 'text-orange-600' : 'text-gray-900'}`}>
                                                ${rate.toFixed(2)}
                                                {isBest && <span className="block md:inline md:ml-2 text-2xs md:text-xs font-semibold uppercase">Best rate</span>}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="border-t border-gray-100 p-5 md:p-6">
                        <div className="flex items-start gap-3">
                            <FaTag className="text-orange-500 mt-1 shrink-0" />
                            <p className="text-sm text-gray-600">
                                Prices are for wall-mounted units and include transport island-wide. Ceiling
                                cassette units are priced separately. First-time customers save up to 20% &mdash;
                                the discounted total is shown before you confirm.{' '}
                                <Link
                                    href="/sg/aircon-servicing-singapore/aircon-servicing-price-singapore"
                                    prefetch={false}
                                    className="text-orange-600 font-semibold hover:underline"
                                >
                                    Aircon servicing price in Singapore &mdash; full price list
                                </Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
